// Budget table and toolbar
import { CUR, grandTotal, moveLine, secTotals } from '../core/budget.js';
import { useEffect, useMemo, useRef, useState } from '../core/react.js';
import { MONO, T, fmt } from '../core/theme.js';
import { ProjectContext } from '../core/project.js';
import { useTableEditor } from '../ui/table-editor.js';
import { Badge, Num, Pill } from '../ui/widgets.js';
import { Icon } from '../ui/icons.js';
import { useNarrow } from '../ui/narrow.js';
import { planByLine } from '../core/subbudgets.js';
import { PlanBar } from '../ui/plan-bar.js';
import { exportBudgetXlsx } from '../core/excel.js';
import { supCat, useSupplierDirectory } from '../core/supplier-directory.js';
import { pageHints } from '../core/page-hints.js';
import { COLS } from '../ui/budget-cols.js';

// How much of a line's in-cost has been spent (costs recorded against it), as a small bar: one colour up to the
// budget, red only past it (costs with no budget on the line are past it too).
function SpentBar({ a, b, dark }) {
  if (a == null) return React.createElement("span", { style: { ...MONO, fontSize: 11, color: dark ? 'rgba(255,255,255,.3)' : T.ivoryDD } }, "\u2014");
  const p = b > 0 ? a / b : null;
  const color = p == null || p > 1.0001 ? T.red : dark ? 'rgba(255,255,255,.75)' : T.navyL;
  const label = p == null ? 'no budget' : Math.round(p * 100) + '%';
  return React.createElement("div", {
    title: `${fmt.gbp(a)} spent of ${fmt.gbp(b)} in-cost` + (p != null && p > 1 ? ` (${fmt.gbp(a - b)} over)` : p != null ? ` (${fmt.gbp(b - a)} left)` : ''),
    style: { display: 'flex', alignItems: 'center', gap: 7 }
  }, React.createElement("div", {
    style: { flex: 1, height: 5, background: dark ? 'rgba(255,255,255,.2)' : T.ivoryD, position: 'relative', overflow: 'hidden', minWidth: 30 }
  }, React.createElement("div", {
    style: { position: 'absolute', left: 0, top: 0, bottom: 0, width: Math.min(100, (p ?? 1) * 100) + '%', background: color }
  })), React.createElement("span", {
    style: { ...MONO, fontSize: 11, minWidth: 34, textAlign: 'right', color: dark ? T.white : (p != null && p > 1.0001 ? T.red : T.coal), fontWeight: p != null && p > 1.0001 ? 600 : 400 }
  }, label));
}
function getCellVal(col, ci) {
  const map = {
    ln: ci.ln,
    desc: ci.desc,
    st: ci.st,
    sup: ci.sup,
    ccy: ci.ccy,
    qty: ci.qty,
    unit: ci.unit,
    up: ci.up,
    vat: ci.vat,
    inGBP: ci.inGBP,
    mu: ci.mu,
    outUSD: ci.outUSD,
    marg: ci.margUSD,
    spent: { a: ci.actInGBP, b: ci.inGBP },
    upUSD: ci.upUSD
  };
  return map[col] ?? null;
}
function BudgetTable({
  sections,
  setSections,
  tab,
  editMode,
  fx,
  subBudgets,
  openPlan,
  readOnly
}) {
  // what each line's sub-budgets plan (Planned column, and the count of lines over on each section)
  const plans = useMemo(() => planByLine(sections, subBudgets, fx).byLine, [sections, subBudgets, fx]);
  const [collapsed, setCollapsed] = useState(new Set());
  const supplierDirectory = useSupplierDirectory();
  const {
    editing,
    editVal,
    setEditVal,
    inputRef,
    startEdit,
    commitEdit,
    commitSelectEdit,
    cancelEdit,
    commitSecName,
    addSection,
    deleteSection,
    addItem,
    deleteItem
  } = useTableEditor({
    sections,
    setSections,
    tab,
    fx,
    supplierDirectory
  });
  const cols = COLS[tab];
  // ── Keyboard: editable cells take focus (Tab, or the arrow keys between them); Enter or F2 edits. While editing,
  // Enter saves and stays on the cell, Tab saves and moves to the next one, Escape cancels.
  const tableRef = useRef(null), refocus = useRef(null);
  const cellKey = (si, ii, k) => `${si}:${ii}:${k}`;
  const editCols = cols.filter(c => c.edit).map(c => c.k);
  const rows = [];
  sections.forEach((sec, si) => { if (!collapsed.has(sec.id)) (sec.items || []).forEach((_, ii) => rows.push([si, ii])); });
  function neighbour(si, ii, k, dir) {
    const r = rows.findIndex(([a, b]) => a === si && b === ii), c = editCols.indexOf(k);
    if (dir === 'right' || dir === 'next') return c < editCols.length - 1 ? [si, ii, editCols[c + 1]] : dir === 'next' && rows[r + 1] ? [...rows[r + 1], editCols[0]] : null;
    if (dir === 'left' || dir === 'prev') return c > 0 ? [si, ii, editCols[c - 1]] : dir === 'prev' && rows[r - 1] ? [...rows[r - 1], editCols[editCols.length - 1]] : null;
    if (dir === 'down') return rows[r + 1] ? [...rows[r + 1], k] : null;
    if (dir === 'up') return rows[r - 1] ? [...rows[r - 1], k] : null;
    return null;
  }
  const focusCell = key => tableRef.current?.querySelector(`[data-cell="${key}"]`)?.focus();
  // after an edit ended from the keyboard, the cursor goes back to that cell (or on to the next)
  useEffect(() => { if (!editing && refocus.current) { const k = refocus.current; refocus.current = null; focusCell(k); } }, [editing]);
  // ── Moving lines (Edit mode): ↑ ↓, "Move to" another section, dragging the handle, or Alt+↑/↓ on a cell
  const moved = useRef(null), dragFrom = useRef(null);
  const [dropAt, setDropAt] = useState(null);   // where a dragged line would land: 'si:ii', or 'si:end'
  // Lines and sections are named by id and found again in the latest state, so a colleague's change arriving while
  // a line is being dragged can't make it move the wrong one. `to`: { before: lineId } (above that line), { by: ±1 }
  // (up or down one), or nothing (the end of the section).
  const move = (lineId, toSecId, to = {}, focusK) => setSections(prev => {
    const si = prev.findIndex(s => (s.items || []).some(i => i.id === lineId)), toSi = prev.findIndex(s => s.id === toSecId);
    if (si < 0 || toSi < 0) return prev;
    const ii = prev[si].items.findIndex(i => i.id === lineId);
    let at = to.by ? ii + to.by : to.before ? prev[toSi].items.findIndex(i => i.id === to.before) : -1;
    if (to.before && at >= 0 && toSi === si && ii < at) at -= 1;
    if (at < 0 && !to.by) at = Infinity;
    if (to.by && (at < 0 || at >= prev[si].items.length)) return prev;
    if (focusK) moved.current = cellKey(toSi, Math.min(at, prev[toSi].items.length - (toSi === si ? 1 : 0)), focusK);
    return moveLine(prev, si, ii, toSi, at);
  });
  useEffect(() => { if (moved.current) { const k = moved.current; moved.current = null; focusCell(k); } }, [sections]);
  // a drop target: above line `beforeId` of section `secId`, or (no line) the end of it; `key` places the marker
  const dropProps = (key, secId, beforeId) => ({
    onDragOver: e => { if (!dragFrom.current) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropAt !== key) setDropAt(key); },
    onDrop: e => {
      const lineId = dragFrom.current; if (!lineId) return;
      e.preventDefault(); dragFrom.current = null; setDropAt(null);
      move(lineId, secId, beforeId ? { before: beforeId } : {});
    }
  });
  function editKeys(e, si, ii, k) {
    if (e.key === 'Escape') { refocus.current = cellKey(si, ii, k); cancelEdit(); }
    if (e.key === 'Enter') { e.preventDefault(); refocus.current = cellKey(si, ii, k); commitEdit(); }
    if (e.key === 'Tab') {
      e.preventDefault();
      const n = neighbour(si, ii, k, e.shiftKey ? 'prev' : 'next');
      refocus.current = n ? cellKey(...n) : cellKey(si, ii, k); commitEdit();
    }
  }
  const toggleSec = id => setCollapsed(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const gt = useMemo(() => grandTotal(sections, fx), [sections, fx]);
  const TH = ({
    col
  }) => React.createElement("th", {
    style: {
      ...MONO,
      width: col.w,
      minWidth: col.w,
      padding: '0 8px',
      height: 32,
      background: T.navy,
      color: 'rgba(255,255,255,.65)',
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: '0.07em',
      textAlign: col.al || 'left',
      whiteSpace: 'nowrap',
      borderRight: `1px solid ${T.navyM}`,
      userSelect: 'none'
    }
  }, col.h);
  function renderCell(col, ci, si, ii, rowBg) {
    const isEditing = editMode && editing?.si === si && editing?.ii === ii && editing?.k === col.k;
    // an out-cost can only be typed when there is an in-cost to mark up
    const noBase = col.k === 'outUSD' && !(ci.rawUSD > 0);
    const isEditable = editMode && col.edit && !noBase;
    const raw = getCellVal(col.k, ci);
    if (isEditing) return React.createElement("td", {
      key: col.k,
      style: {
        padding: '2px 4px',
        height: 34,
        background: '#EBF3FF',
        borderBottom: `2px solid ${T.blue}`,
        borderRight: `1px solid ${T.rule}`,
        minWidth: col.w
      }
    }, col.type === 'select' ? React.createElement("select", {
      ref: inputRef,
      value: editVal,
      onChange: e => {
        const v = e.target.value;
        setEditVal(v);
        refocus.current = cellKey(si, ii, col.k);
        setTimeout(() => commitSelectEdit(v), 0);
      },
      onKeyDown: e => {
        if (e.key === 'Escape') { refocus.current = cellKey(si, ii, col.k); cancelEdit(); }
      },
      'aria-label': col.h,
      autoFocus: true,
      style: {
        width: '100%',
        background: 'transparent',
        border: 'none',
        fontSize: 11,
        color: T.coal,
        padding: '2px',
        fontFamily: "'Open Sans',system-ui,sans-serif",
        cursor: 'pointer'
      }
    }, (col.opts === '_fx_' ? Object.keys(fx) : col.opts || []).map(s => React.createElement("option", {
      key: s,
      value: s
    }, s))) : React.createElement(React.Fragment, null, React.createElement("input", {
      ref: inputRef,
      value: editVal,
      // the supplier column suggests names from the supplier database; picking one links the line to it
      list: col.k === 'sup' ? 'prism-supplier-names' : undefined,
      onChange: e => setEditVal(e.target.value),
      onBlur: commitEdit,
      onKeyDown: e => editKeys(e, si, ii, col.k),
      'aria-label': col.h,
      // numbers get the phone's number keypad
      inputMode: col.type === 'text' || col.k === 'desc' || col.k === 'sup' || col.k === 'unit' ? undefined : 'decimal',
      enterKeyHint: 'done',
      style: {
        ...MONO,
        width: '100%',
        background: 'transparent',
        border: 'none',
        fontSize: 11,
        textAlign: col.al || 'left',
        padding: '2px',
        fontFamily: col.type === 'text' ? "'Open Sans',system-ui,sans-serif" : undefined
      }
    }), col.k === 'sup' && React.createElement("datalist", { id: 'prism-supplier-names' },
      supplierDirectory.map(d => React.createElement("option", { key: d.id, value: d.name }, [d.city, d.cat && supCat(d.cat)].filter(Boolean).join(' · '))))));
    return React.createElement("td", {
      key: col.k,
      onClick: () => isEditable && startEdit(si, ii, col.k, raw, editMode),
      title: editMode && noBase ? 'Add a quantity and unit price first: the out-cost sets the markup on the in-cost' : editMode && col.k === 'outUSD' ? 'Type an out-cost and the markup is set to match' : undefined,
      ...(isEditable ? {
        tabIndex: 0,
        'data-cell': cellKey(si, ii, col.k),
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); startEdit(si, ii, col.k, raw, editMode); return; }
          if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            e.preventDefault();
            const to = ii + (e.key === 'ArrowUp' ? -1 : 1);
            if (to >= 0 && to < sections[si].items.length) move(ci.id, sections[si].id, { by: to - ii }, col.k);
            return;
          }
          const dir = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' }[e.key];
          const n = dir && neighbour(si, ii, col.k, dir);
          if (n) { e.preventDefault(); focusCell(cellKey(...n)); }
        }
      } : {}),
      style: {
        padding: '0 8px',
        height: 34,
        textAlign: col.al || 'left',
        borderRight: `1px solid ${T.rule}`,
        borderBottom: `1px solid ${T.ivoryD}`,
        whiteSpace: 'nowrap',
        minWidth: col.w,
        ...(isEditable ? {
          cursor: 'text',
          outline: 'none'
        } : {})
      }
    }, col.k === 'ln' && React.createElement("span", {
      style: {
        ...MONO,
        fontSize: 9,
        color: T.mist
      }
    }, raw), col.k === 'st' && React.createElement(Badge, {
      s: raw
    }), col.k === 'desc' && React.createElement("span", {
      style: {
        fontSize: 12,
        color: T.coal,
        fontFamily: "'Open Sans',system-ui,sans-serif",
        textDecoration: isEditable ? 'underline dotted' : undefined,
        textDecorationColor: T.rule
      }
    }, raw || React.createElement("span", {
      style: {
        color: T.ivoryDD
      }
    }, "\u2014")), col.k === 'sup' && React.createElement("span", {
      style: {
        fontSize: 11,
        color: T.mist
      }
    }, raw || React.createElement("span", {
      style: {
        color: T.ivoryDD
      }
    }, "\u2014")), col.k === 'ccy' && React.createElement(Pill, {
      label: raw
    }), col.k === 'unit' && React.createElement("span", {
      style: {
        fontSize: 10,
        color: T.mist
      }
    }, raw || React.createElement("span", {
      style: {
        color: T.ivoryDD
      }
    }, "\u2014")), col.k === 'qty' && React.createElement(Num, {
      v: raw
    }), ['up', 'upUSD'].includes(col.k) && React.createElement(Num, {
      v: raw
    }), col.k === 'vat' && React.createElement("span", {
      style: {
        ...MONO,
        fontSize: 11,
        color: raw ? T.coal : T.mist
      }
    }, raw != null ? Math.round(raw * 10000) / 100 + '%' : '0%'), col.k === 'mu' && React.createElement(Num, {
      v: raw,
      f: "pct"
    }), col.k === 'inGBP' && React.createElement("span", {
      title: ci.ccy !== CUR.cost ? `${fmt.gbp(raw)} in ${CUR.cost}` : undefined,
      style: { ...MONO, fontSize: 11 }
    }, fmt.ccy(ci.net, ci.ccy)), col.k === 'plan' && (plans.get(ci.lineId)
      ? React.createElement("button", { type: 'button', onClick: () => openPlan && openPlan(ci.lineId), title: 'Open the sub-budget', style: { display: 'block', width: '100%', textAlign: 'left' } }, React.createElement(PlanBar, { plan: plans.get(ci.lineId) }))
      : openPlan && !readOnly && ci.lineId && React.createElement("button", { type: 'button', onClick: () => openPlan(ci.lineId), 'aria-label': `Break down ${ci.desc || 'this line'} into a sub-budget`, style: { fontSize: 10.5, color: T.slate, border: `1px dashed ${T.rule}`, padding: '2px 7px' } }, 'Break down')),
    col.k === 'spent' && React.createElement(SpentBar, raw), col.k === 'outUSD' && React.createElement(Num, {
      v: raw,
      f: "usd"
    }), col.k === 'marg' && React.createElement(Num, {
      v: raw,
      f: "usd"
    }));
  }
  return React.createElement("div", {
    style: {
      overflowX: 'auto',
      overflowY: 'auto',
      flex: 1,
      position: 'relative'
    }
  }, React.createElement("table", {
    ref: tableRef,
    style: {
      minWidth: cols.reduce((s, c) => s + (c.w || 80), 0) + (editMode ? 190 : 20),
      borderCollapse: 'collapse'
    }
  }, React.createElement("thead", {
    style: {
      position: 'sticky',
      top: 0,
      zIndex: 10
    }
  }, React.createElement("tr", null, editMode && React.createElement("th", {
    style: {
      width: 180,
      minWidth: 180,
      background: T.navy,
      borderRight: `1px solid ${T.navyM}`
    }
  }), cols.map(col => React.createElement(TH, {
    key: col.k,
    col: col
  })))), React.createElement("tbody", null, sections.map((sec, si) => {
    const tot = secTotals(sec.items, fx);
    const isCollapsed = collapsed.has(sec.id);
    return React.createElement(React.Fragment, {
      key: sec.id
    }, React.createElement("tr", {
      ...(editMode ? dropProps(`${si}:0`, sec.id, sec.items?.[0]?.id) : {}),
      style: {
        cursor: 'pointer',
        userSelect: 'none',
        boxShadow: dropAt === `${si}:0` && !(sec.items || []).length ? `inset 0 -3px 0 ${T.blue}` : undefined
      }
    }, React.createElement("td", {
      colSpan: cols.length + (editMode ? 1 : 0),
      style: {
        background: T.navyM,
        padding: '0 12px',
        height: 32,
        borderBottom: `1px solid ${T.navyL}`
      }
    }, React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }
    }, React.createElement("button", {
      type: 'button',
      onClick: () => toggleSec(sec.id),
      'aria-expanded': !isCollapsed,
      'aria-label': `${isCollapsed ? 'Show' : 'Hide'} the lines of ${sec.name}`,
      style: {
        color: 'rgba(255,255,255,.5)',
        padding: 2,
        display: 'flex',
        transition: 'transform .2s',
        transform: isCollapsed ? 'rotate(-90deg)' : 'none',
        flexShrink: 0
      }
    }, React.createElement(Icon.chev, {
      s: 11,
      c: "rgba(255,255,255,.5)",
      d: "down"
    })), editMode ? React.createElement("input", {
      defaultValue: sec.name,
      'aria-label': 'Section name',
      onBlur: e => commitSecName(si, e.target.value),
      onKeyDown: e => {
        if (e.key === 'Enter') e.target.blur();
      },
      style: {
        background: 'transparent',
        border: 'none',
        borderBottom: '1px solid rgba(255,255,255,.3)',
        fontSize: 10,
        fontWeight: 700,
        color: T.white,
        letterSpacing: '0.1em',
        width: 220,
        padding: '1px 4px',
        outline: 'none'
      }
    }) : React.createElement("span", {
      onClick: () => toggleSec(sec.id),
      style: {
        cursor: 'pointer',
        fontSize: 10,
        fontWeight: 700,
        color: T.white,
        letterSpacing: '0.1em'
      }
    }, sec.name), React.createElement("span", {
      style: {
        marginLeft: 'auto',
        ...MONO,
        fontSize: 10,
        color: 'rgba(255,255,255,.5)'
      }
    }, (n => n > 0 && React.createElement("span", {
      style: { fontSize: 10, fontWeight: 700, color: '#FFB4AB', marginRight: 10, fontFamily: "'Open Sans',system-ui,sans-serif" }
    }, `${n} line${n > 1 ? 's' : ''} over plan`))((sec.items || []).filter(it => plans.get(it.lineId)?.over).length), fmt.gbp(tot.inGBP), " \u2192 ", fmt.usd(tot.outUSD)), editMode && React.createElement("button", {
      onClick: () => deleteSection(si),
      style: {
        background: 'rgba(255,60,60,.18)',
        border: 'none',
        color: '#FF8080',
        fontSize: 10,
        padding: '2px 8px',
        cursor: 'pointer',
        borderRadius: 2,
        flexShrink: 0,
        marginLeft: 6
      }
    }, "\u2715 Delete Section")))), !isCollapsed && tot.items.map((ci, ii) => {
      const rowBg = ii % 2 === 0 ? T.white : T.ivory;
      const n = sections[si].items.length, label = `line ${ci.ln || ii + 1} ${ci.desc || ''}`.trim();
      const tool = { background: 'none', border: 'none', color: T.mist, fontSize: 13, cursor: 'pointer', padding: '2px 4px', lineHeight: 1 };
      return React.createElement("tr", {
        key: ci.id,
        ...(editMode ? dropProps(`${si}:${ii}`, sec.id, ci.id) : {}),
        onMouseEnter: e => e.currentTarget.style.background = T.ivoryD,
        onMouseLeave: e => e.currentTarget.style.background = rowBg,
        style: {
          background: rowBg,
          transition: 'background .1s',
          boxShadow: dropAt === `${si}:${ii}` ? `inset 0 3px 0 ${T.blue}` : undefined
        }
      }, editMode && React.createElement("td", {
        style: {
          width: 180,
          padding: '0 6px',
          borderRight: `1px solid ${T.rule}`,
          borderBottom: `1px solid ${T.ivoryD}`,
          whiteSpace: 'nowrap'
        }
      }, React.createElement("span", {
        draggable: true,
        onDragStart: e => { dragFrom.current = ci.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', ci.desc || ''); },
        onDragEnd: () => { dragFrom.current = null; setDropAt(null); },
        title: 'Drag to move this line',
        'aria-hidden': true,
        style: { ...tool, cursor: 'grab', display: 'inline-block', padding: '2px 5px', userSelect: 'none' }
      }, "\u2807"), React.createElement("button", {
        onClick: () => move(ci.id, sec.id, { by: -1 }),
        disabled: ii === 0,
        'aria-label': `Move ${label} up`,
        title: 'Move up (Alt+\u2191)',
        style: { ...tool, opacity: ii === 0 ? .3 : 1 }
      }, "\u2191"), React.createElement("button", {
        onClick: () => move(ci.id, sec.id, { by: 1 }),
        disabled: ii === n - 1,
        'aria-label': `Move ${label} down`,
        title: 'Move down (Alt+\u2193)',
        style: { ...tool, opacity: ii === n - 1 ? .3 : 1 }
      }, "\u2193"), sections.length > 1 && React.createElement("select", {
        value: '',
        onChange: e => { const to = sections[Number(e.target.value)]; if (e.target.value !== '' && to) move(ci.id, to.id); },
        'aria-label': `Move ${label} to another section`,
        title: 'Move to another section',
        style: { fontSize: 11, color: T.slate, border: `1px solid ${T.rule}`, background: T.white, padding: '1px 2px', width: 82, margin: '0 2px' }
      }, React.createElement("option", { value: '' }, "Move to\u2026"), sections.map((s2, s2i) => s2i !== si && React.createElement("option", { key: s2.id, value: s2i }, s2.name || 'Section'))), React.createElement("button", {
        onClick: () => deleteItem(si, ii),
        'aria-label': `Delete ${label}`,
        style: tool,
        title: "Delete item"
      }, "\u2715")), cols.map(col => renderCell(col, ci, si, ii, rowBg)));
    }), !isCollapsed && editMode && React.createElement("tr", {
      ...dropProps(`${si}:end`, sec.id),
      style: { boxShadow: dropAt === `${si}:end` ? `inset 0 3px 0 ${T.blue}` : undefined }
    }, React.createElement("td", {
      colSpan: cols.length + 1,
      style: {
        padding: '4px 12px',
        background: T.ivory,
        borderBottom: `1px solid ${T.rule}`
      }
    }, React.createElement("button", {
      onClick: () => addItem(si),
      style: {
        background: 'none',
        border: `1px dashed ${T.rule}`,
        color: T.slate,
        fontSize: 11,
        padding: '4px 14px',
        cursor: 'pointer',
        transition: 'all .15s'
      },
      onMouseEnter: e => {
        e.currentTarget.style.borderColor = T.navy;
        e.currentTarget.style.color = T.navy;
      },
      onMouseLeave: e => {
        e.currentTarget.style.borderColor = T.rule;
        e.currentTarget.style.color = T.slate;
      }
    }, "+ Add Line Item"))), !isCollapsed && React.createElement("tr", {
      style: {
        background: '#E0E9F0'
      }
    }, editMode && React.createElement("td", {
      style: {
        borderBottom: `2px solid ${T.navy}`
      }
    }), cols.map((col, ci) => React.createElement("td", {
      key: col.k,
      style: {
        padding: '0 8px',
        height: 28,
        textAlign: col.al || 'left',
        borderRight: `1px solid ${T.rule}`,
        borderBottom: `2px solid ${T.navy}`,
        minWidth: col.w
      }
    }, ci === 1 && React.createElement("span", {
      style: {
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.1em',
        color: T.navyM
      }
    }, "SUBTOTAL"), col.k === 'inGBP' && React.createElement(Num, {
      v: tot.inGBP,
      f: "gbp"
    }), col.k === 'spent' && React.createElement(SpentBar, {
      a: tot.actInGBP,
      b: tot.inGBP
    }), col.k === 'outUSD' && React.createElement(Num, {
      v: tot.outUSD,
      f: "usd"
    }), col.k === 'marg' && React.createElement(Num, {
      v: tot.margUSD,
      f: "usd"
    }), col.k === 'mu' && tot.rawUSD > 0 && React.createElement(Num, {
      v: tot.margUSD / tot.rawUSD,
      f: "pct"
    })))));
  }), editMode && React.createElement("tr", null, React.createElement("td", {
    colSpan: cols.length + 1,
    style: {
      padding: '8px 12px',
      background: T.ivoryD
    }
  }, React.createElement("button", {
    onClick: addSection,
    style: {
      background: 'none',
      border: `1px dashed ${T.navy}`,
      color: T.navy,
      fontSize: 11,
      fontWeight: 600,
      padding: '5px 18px',
      cursor: 'pointer',
      letterSpacing: '0.06em',
      transition: 'all .15s'
    },
    onMouseEnter: e => e.currentTarget.style.background = T.ivory,
    onMouseLeave: e => e.currentTarget.style.background = 'none'
  }, "+ Add Section"))), React.createElement("tr", {
    style: {
      background: T.navy
    }
  }, editMode && React.createElement("td", {
    style: {
      background: T.navy
    }
  }), cols.map((col, ci) => React.createElement("td", {
    key: col.k,
    style: {
      padding: '0 8px',
      height: 42,
      whiteSpace: 'nowrap',   // a total stays on one line: the column widens rather than "CHF" above the number
      textAlign: col.al || 'left',
      borderRight: ci < cols.length - 1 ? `1px solid ${T.navyM}` : 'none',
      minWidth: col.w
    }
  }, ci === 1 && React.createElement("span", {
    style: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.1em',
      color: T.white
    }
  }, "GRAND TOTAL"), col.k === 'inGBP' && React.createElement("span", {
    style: {
      ...MONO,
      fontSize: 13,
      fontWeight: 500,
      color: T.white
    }
  }, fmt.gbp(gt.inGBP)), col.k === 'spent' && React.createElement(SpentBar, {
    a: gt.actInGBP,
    b: gt.inGBP,
    dark: true
  }), col.k === 'outUSD' && React.createElement("span", {
    style: {
      ...MONO,
      fontSize: 13,
      fontWeight: 500,
      color: T.white
    }
  }, fmt.usd(gt.outUSD)), col.k === 'marg' && React.createElement("span", {
    style: {
      ...MONO,
      fontSize: 13,
      fontWeight: 400,
      color: 'rgba(255,255,255,.7)'
    }
  }, fmt.usd(gt.margUSD))))))));
}
const budgetBarBtn = { padding: '6px 12px', background: T.white, color: T.coal, border: `1px solid ${T.rule}`, fontSize: 12, fontWeight: 500, cursor: 'pointer' };
export function BudgetView({
  project,
  sections,
  setSections,
  fx,
  setView,
  flashSync,
  user,
  viewAs,
  viewChoices,
  setViewAs
}) {
  const [tab, setTab] = useState('budget');
  const [editing, setEdit] = useState(false);
  const narrow = useNarrow();
  const gt = useMemo(() => grandTotal(sections, fx), [sections, fx]);
  const isReadOnly = React.useContext(ProjectContext)?.user?.role === 'Read-only';
  const tabs = [{
    id: 'budget',
    label: 'Budget'
  }, {
    // costs come from the Costs page and show in the Spent column; the old Actuals & Variance tab is gone
    id: 'client',
    label: 'Client View'
  }];
  function handleEdit() {
    if (isReadOnly) return;
    setEdit(!editing);
  }
  // fills what is left under anything shown above it (the approvals bar), so the totals bar stays on screen
  return React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0
    }
  }, editing && isReadOnly && React.createElement("div", {
    style: {
      padding: '8px 22px',
      background: T.amberBg,
      borderBottom: `1px solid ${T.amberBr}`,
      fontSize: 12,
      color: T.amber,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexShrink: 0
    }
  }, React.createElement("span", {
    style: {
      fontWeight: 700
    }
  }, "\u26A0 View only"), " \u2014 Your account can look at budgets but not change them."), React.createElement("div", {
    // on a phone the tabs take the first line and the buttons wrap underneath
    style: {
      background: T.white,
      borderBottom: `1px solid ${T.rule}`,
      padding: narrow ? '0 10px 8px' : '0 22px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: narrow ? 'wrap' : 'nowrap',
      flexShrink: 0,
      minHeight: 50
    }
  }, React.createElement("div", {
    style: {
      display: 'flex',
      gap: 0,
      ...(narrow ? { width: '100%', overflowX: 'auto' } : {})
    }
  }, tabs.map(t => React.createElement("button", {
    key: t.id,
    onClick: () => setTab(t.id),
    'aria-pressed': tab === t.id,
    style: {
      padding: narrow ? '0 10px' : '0 18px',
      whiteSpace: 'nowrap',
      height: narrow ? 44 : 50,
      background: 'none',
      border: 'none',
      fontSize: 13,
      fontWeight: tab === t.id ? 600 : 400,
      color: tab === t.id ? T.navy : T.mist,
      borderBottom: `2px solid ${tab === t.id ? T.navy : 'transparent'}`,
      transition: 'all .15s',
      cursor: 'pointer'
    }
  }, t.label))), React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: narrow ? 8 : 12,
      flexWrap: 'wrap'
    }
  }, editing && React.createElement("span", {
    style: {
      fontSize: 11,
      fontWeight: 600,
      color: T.amber,
      letterSpacing: '0.05em'
    }
  }, "\u25CF EDITING"), setViewAs && React.createElement("label", {
    title: 'Show every figure on this page in one currency. Only changes what you see: the project\'s cost and billing currencies stay as set.',
    style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: viewAs ? T.amber : T.slate, fontWeight: 600 }
  }, "View in", React.createElement("select", {
    value: viewAs || '',
    onChange: e => setViewAs(e.target.value),
    style: { ...budgetBarBtn, padding: '5px 6px', borderColor: viewAs ? T.amber : T.rule }
  }, React.createElement("option", { value: '' }, `${CUR.own?.cost} / ${CUR.own?.bill} (project)`), (viewChoices || []).map(c => React.createElement("option", { key: c, value: c }, c)))), setView && !narrow && React.createElement("button", {
    onClick: () => setView('project'),
    title: 'Name, client, date, status, currencies, Drive folder and who can see this project',
    style: budgetBarBtn
  }, "Project settings"), setView && !isReadOnly && React.createElement("button", {
    onClick: () => { pageHints.scrollToImport = true; setView('project'); },
    title: 'Replace this budget with lines from an .xlsx file',
    style: budgetBarBtn
  }, "Import"), React.createElement("button", {
    onClick: () => exportBudgetXlsx(project, user).catch(e => flashSync?.(e.message || 'Export failed', 'err')),
    disabled: !sections.some(s => (s.items || []).length),
    title: 'Download this budget as an Excel file',
    style: budgetBarBtn
  }, "Export .xlsx"), tab !== 'client' && React.createElement("button", {
    onClick: handleEdit,
    style: {
      padding: '6px 15px',
      background: editing ? T.navy : T.ivory,
      color: editing ? T.white : T.coal,
      border: `1px solid ${editing ? T.navy : T.rule}`,
      fontSize: 12,
      fontWeight: 500,
      transition: 'all .15s',
      cursor: 'pointer'
    }
  }, editing ? 'Done Editing' : '✎ Edit Budget'), React.createElement("div", {
    style: {
      height: 18,
      width: 1,
      background: T.rule
    }
  }), React.createElement("span", {
    style: {
      ...MONO,
      fontSize: 11,
      color: T.mist
    }
  }, fmt.usd(gt.outUSD), " ", React.createElement("span", {
    style: {
      color: T.slate
    }
  }, "out-cost")))), React.createElement(BudgetTable, {
    sections: sections,
    setSections: setSections,
    tab: tab,
    editMode: editing,
    fx: fx,
    subBudgets: project?.subBudgets,
    readOnly: isReadOnly,
    openPlan: setView ? lineId => { pageHints.subBudgetFor = lineId; setView('subbudgets'); } : null
  }), React.createElement("div", {
    style: {
      background: T.navy,
      flexShrink: 0,
      padding: '12px 22px',
      display: 'flex',
      gap: 0,
      alignItems: 'center',
      flexWrap: 'wrap'
    }
  }, [['TOTAL IN-COST', fmt.gbp(gt.inGBP), CUR.cost], ['TOTAL OUT-COST', fmt.usd(gt.outUSD), CUR.bill], ['PROJECTED MARGIN', fmt.usd(gt.margUSD) + '  ·  ' + (gt.outUSD > 0 ? fmt.pct(gt.margUSD / gt.outUSD) : '—'), ''], ...(gt.actInGBP != null ? [['ACTUAL IN-COST', fmt.gbp(gt.actInGBP), CUR.cost]] : [])].map(([l, v, s], i) => React.createElement("div", {
    key: l,
    style: {
      padding: '0 22px',
      whiteSpace: 'nowrap',
      borderRight: `1px solid rgba(255,255,255,.1)`
    }
  }, React.createElement("div", {
    style: {
      fontSize: 8,
      letterSpacing: '0.12em',
      color: 'rgba(255,255,255,.35)',
      marginBottom: 3
    }
  }, l), React.createElement("div", {
    style: {
      ...MONO,
      fontSize: 13,
      fontWeight: 500,
      color: T.white
    }
  }, v), s && React.createElement("div", {
    style: {
      fontSize: 9,
      color: 'rgba(255,255,255,.3)',
      marginTop: 2
    }
  }, s)))));
}
